import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, ConfirmButton, PageHeader, SelectField, Spinner, TextField } from '../../components/ui';
import { Tabs, useTab } from '../sites/Tabs';
import { timeZoneNames } from '../sites/api';
import { BASE, SrBuilding, keys } from './api';
import type { SrBuildingUpdate } from './api';
import { DevicesTab } from './DevicesTab';
import { EvacuationsTab } from './EvacuationsTab';
import { InvitationsTab } from './InvitationsTab';
import { OrderTab } from './OrderTab';
import { PhonesTab } from './PhonesTab';
import { StaffTab } from './StaffTab';

const TAB_KEYS = ['details', 'invitations', 'order', 'staff', 'devices', 'phones', 'evacuations'] as const;
type TabKey = (typeof TAB_KEYS)[number];
export const BUILDING_TABS = TAB_KEYS.map((key) => ({ key, label: t(`srp.tab.${key}`) }));

function DetailsTab({ building, canManage }: { building: SrBuilding; canManage: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const initial = {
    name: building.name,
    address: building.address,
    timeZone: building.timeZone,
    operatorName: building.operatorName,
    operatorContactEmail: building.operatorContactEmail,
    alertEmail: building.alertEmail,
  };
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [building]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const editable = canManage && building.status === 'active';
  const save = useMutation({
    mutationFn: () => {
      const body: SrBuildingUpdate = { ...form };
      return api.patch(`${BASE}/${building.id}`, { body, schema: SrBuilding });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['shared-reception', 'platform'] });
      toast(t('srp.details.saved'));
    },
  });
  const archive = useMutation({
    mutationFn: () => api.post(`${BASE}/${building.id}/archive`, { schema: SrBuilding }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['shared-reception', 'platform'] });
      toast(t('srp.details.archiveDone'));
    },
  });
  const errs = fieldErrors(save.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <div className="space-y-6">
      <Card
        title={t('srp.tab.details')}
        description={t('srp.tab.details.desc')}
        footer={
          editable ? (
            <Button type="submit" form="srp-details-form" variant="primary" disabled={!dirty || save.isPending}>
              {t('srp.details.save')}
            </Button>
          ) : null
        }
      >
        <form id="srp-details-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
          <TextField label={t('srp.field.name')} value={form.name} onChange={set('name')} error={errs['name']} disabled={!editable} />
          <TextField label={t('srp.field.address')} value={form.address} onChange={set('address')} error={errs['address']} disabled={!editable} />
          <SelectField label={t('srp.field.timeZone')} hint={t('srp.field.timeZoneHint')} value={form.timeZone} onChange={set('timeZone')} error={errs['timeZone']} disabled={!editable}>
            {timeZoneNames(form.timeZone).map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </SelectField>
          <TextField label={t('srp.field.operatorName')} hint={t('srp.field.operatorNameHint')} value={form.operatorName} onChange={set('operatorName')} error={errs['operatorName']} disabled={!editable} />
          <TextField label={t('srp.field.operatorEmail')} type="email" value={form.operatorContactEmail} onChange={set('operatorContactEmail')} error={errs['operatorContactEmail']} disabled={!editable} />
          <TextField label={t('srp.field.alertEmail')} hint={t('srp.field.alertEmailHint')} type="email" value={form.alertEmail} onChange={set('alertEmail')} error={errs['alertEmail']} disabled={!editable} />
        </form>
        <ErrorLine error={Object.keys(errs).length ? null : save.error} />
        <p className="mt-4 text-sm text-slate-700">
          <span className="font-medium">{t('srp.details.pin')}: </span>
          {building.pinSet ? t('srp.details.pinSet') : t('srp.details.pinNotSet')}
        </p>
      </Card>
      {canManage && building.status === 'active' ? (
        <Card title={t('srp.details.archive')} description={t('srp.details.archiveHint')}>
          <ErrorLine error={archive.error} />
          <ConfirmButton label={t('srp.details.archive')} onConfirm={() => archive.mutate()} pending={archive.isPending} />
        </Card>
      ) : null}
    </div>
  );
}

/** Platform: one shared reception, with a tab for each part of it (same tab style as Platform > Settings). */
export function BuildingDetailPage() {
  const { id = '' } = useParams();
  const canManage = useCan('platform.shared_reception_manage');
  const [tab, setTab] = useTab(BUILDING_TABS);
  const current = tab as TabKey;
  const q = useQuery({ queryKey: keys.building(id), queryFn: () => api.get(`${BASE}/${id}`, { schema: SrBuilding }) });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  const b = q.data;
  const active = b.status === 'active';
  return (
    <>
      <p className="mb-2 text-sm">
        <Link className="font-medium text-brand hover:underline" to="/platform/shared-reception">
          {t('srp.back')}
        </Link>
      </p>
      <PageHeader
        title={
          <>
            {b.name} <Badge tone={active ? 'good' : 'neutral'}>{t(`srp.status.${b.status}`)}</Badge>
          </>
        }
        description={b.address}
      />
      {!active ? <Alert kind="info">{t('srp.details.archived')}</Alert> : null}
      <Tabs tabs={BUILDING_TABS} current={current} onChange={setTab} label={t('srp.detail.tabs')}>
        <p className="mb-4 text-sm text-slate-600">{t(`srp.tab.${current}.desc`)}</p>
        {current === 'details' ? <DetailsTab building={b} canManage={canManage} /> : null}
        {current === 'invitations' ? <InvitationsTab building={b} canManage={canManage} /> : null}
        {current === 'order' ? <OrderTab building={b} canManage={canManage} /> : null}
        {current === 'staff' ? <StaffTab building={b} canManage={canManage} /> : null}
        {current === 'devices' ? <DevicesTab building={b} canManage={canManage} /> : null}
        {current === 'phones' ? <PhonesTab building={b} canManage={canManage} /> : null}
        {current === 'evacuations' ? <EvacuationsTab building={b} /> : null}
      </Tabs>
    </>
  );
}
